import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useHasRole, useJob, useJobSearch, useRetryJob } from '../api/hooks';
import { JOB_STATE_LIST, JOB_TYPES, type JobDetail, type JobState } from '../api/types';
import { PageHeader } from '../app/Shell';
import { JOB_STATE } from '../app/labels';
import { Badge, Button, Card, ConfirmDialog, DataTable, Drawer, ErrorNote, Id, JsonView, KeyValues, QueryView, ReasonField, Section, SuccessNote, fmtAgo, fmtDateTime, fmtUsd, reasonError, toast } from '../ui';
import { LoadMore } from './parts';

const JOB_TYPE_LABEL: Record<(typeof JOB_TYPES)[number], string> = {
  'story.generate': 'AI 章節撰寫',
  'comic.render': '漫畫繪製',
  'shift.pack': '打工小遊戲題庫',
  'npc.reply': '角色回覆訊息',
  'data.export': '資料匯出',
  'data.delete': '帳戶刪除',
  'prereg.launch': '上線通知電郵',
};

/** Background work with filters; open a job for its error, payload and steps, and retry a failed one. */
export function JobsPage() {
  const [params, setParams] = useSearchParams();
  const state = params.get('state') ?? 'failed_permanent';
  const type = params.get('type') ?? '';
  const userId = params.get('user') ?? '';
  const open = params.get('job');
  const list = useJobSearch({ state: state === 'all' ? undefined : state, type: type || undefined, userId: userId.trim() || undefined });

  const set = (next: Record<string, string | null>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v) p.set(k, v);
      else p.delete(k);
    }
    setParams(p, { replace: true });
  };

  return (
    <>
      <PageHeader title="Jobs 佇列" description="背景工作：AI 章節、漫畫繪製、角色回覆、資料匯出等。預設顯示永久失敗的，最新的在前。" />
      <Card flush>
        <div className="filters">
          <label>
            狀態
            <select value={state} onChange={(e) => set({ state: e.target.value })}>
              <option value="all">全部</option>
              <option value="active">進行中（排隊 / 執行 / 等待重試）</option>
              {JOB_STATE_LIST.map((s) => (
                <option key={s} value={s}>
                  {JOB_STATE[s].label}
                </option>
              ))}
            </select>
          </label>
          <label>
            類型
            <select value={type} onChange={(e) => set({ type: e.target.value })}>
              <option value="">全部</option>
              {JOB_TYPES.map((t) => (
                <option key={t} value={t}>
                  {JOB_TYPE_LABEL[t]}（{t}）
                </option>
              ))}
            </select>
          </label>
          <label>
            玩家 ID
            <input type="text" defaultValue={userId} onBlur={(e) => set({ user: e.target.value.trim() })} onKeyDown={(e) => e.key === 'Enter' && set({ user: e.currentTarget.value.trim() })} placeholder="user ID" />
          </label>
        </div>
        <QueryView query={list} loading="載入 jobs…">
          {() => (
            <>
              <DataTable pageSize={25}
                rows={list.rows}
                rowKey={(j) => j._id}
                onRowClick={(j) => set({ job: j._id })}
                selectedKey={open}
                empty="沒有符合的 job。"
                columns={[
                  { key: 'at', header: '更新', render: (j) => <span title={fmtDateTime(j.updatedAt)} className="nowrap">{fmtAgo(j.updatedAt)}</span> },
                  { key: 'type', header: '類型', render: (j) => <code>{j.type}</code> },
                  { key: 'state', header: '狀態', render: (j) => <Badge tone={JOB_STATE[j.state].tone}>{JOB_STATE[j.state].label}</Badge> },
                  { key: 'attempt', header: '嘗試', align: 'right', render: (j) => j.attempt },
                  { key: 'user', header: '玩家', render: (j) => (j.userId ? <Id value={j.userId} /> : <span className="faint">—</span>) },
                  { key: 'error', header: '錯誤', render: (j) => <span className="truncate" style={{ display: 'block', maxWidth: 420 }} title={j.error ?? ''}>{j.error ?? <span className="faint">—</span>}</span> },
                ]}
              />
              <LoadMore q={list} />
            </>
          )}
        </QueryView>
      </Card>
      <Drawer open={!!open} title="Job" onClose={() => set({ job: null })}>
        {open && <JobView id={open} />}
      </Drawer>
    </>
  );
}

function JobView({ id }: { id: string }) {
  const q = useJob(id);
  return <QueryView query={q}>{(j) => <JobBody j={j} />}</QueryView>;
}

function JobBody({ j }: { j: JobDetail }) {
  const canRetry = useHasRole('support');
  const [confirm, setConfirm] = useState(false);
  const [reason, setReason] = useState('');
  const retry = useRetryJob();
  const state = j.state as JobState;
  return (
    <>
      <KeyValues
        items={[
          ['Job ID', <code>{j._id}</code>],
          ['類型', <code>{j.type}</code>],
          ['狀態', <Badge tone={JOB_STATE[state].tone}>{JOB_STATE[state].label}</Badge>],
          ['嘗試', `${j.attempt} / ${j.maxAttempts ?? '—'}`],
          ['優先次序', j.priority ?? '—'],
          ['階段', j.phase ?? '—'],
          ['玩家', j.userId ? <Link to={`/users/${j.userId}`}>{j.userId}</Link> : '—'],
          ['成本', typeof j['costMicrousd'] === 'number' ? fmtUsd((j['costMicrousd'] as number) / 1e6) : '—'],
          ['建立', fmtDateTime(j.createdAt)],
          ['更新', fmtDateTime(j.updatedAt)],
        ]}
      />
      {j.error && (
        <Section title="錯誤">
          <pre className="diff">{j.error}</pre>
        </Section>
      )}
      {state === 'failed_permanent' && (
        <Section title="重試">
          {retry.isSuccess ? (
            <SuccessNote>已重新排隊。</SuccessNote>
          ) : (
            <Button variant="primary" disabled={!canRetry} title={canRetry ? undefined : '需要客服角色'} onClick={() => setConfirm(true)}>
              重新排隊
            </Button>
          )}
        </Section>
      )}
      <Section title="Payload">
        <JsonView value={j.payload} />
      </Section>
      <Section title="Steps">
        <JsonView value={j['steps']} />
      </Section>
      <ConfirmDialog
        open={confirm}
        title="重試這個 job？"
        confirmLabel="重新排隊"
        busy={retry.isPending}
        canConfirm={!reasonError(reason)}
        onCancel={() => setConfirm(false)}
        onConfirm={() =>
          retry.mutate(
            { jobId: j._id, reason: reason.trim() },
            {
              onSuccess: () => {
                setConfirm(false);
                toast('已重新排隊');
              },
            },
          )
        }
        error={retry.isError ? <ErrorNote error={retry.error} /> : null}
      >
        <p className="muted small">嘗試次數歸零並立即重新排隊。AI 類 job 會再次產生花費。</p>
        <ReasonField value={reason} onChange={setReason} autoFocus />
      </ConfirmDialog>
    </>
  );
}
